// The six numbers an order carries, in ONE place, because searching by a number
// and showing that number are two halves of one promise. Built at Session 79.14d
// for #304's gate (D6, D13, D15, D16) and #545.
//
// WHY THIS FILE EXISTS AT ALL. Three screens already searched orders and all
// three had independently written their own field list — RequestsInboxPage.js
// says so in its own comment, that it copied Invoicing's list rather than
// inventing a fourth. They had drifted anyway: Invoicing searched the vendor's
// order number and the Requests inbox did not; the Requests inbox searched the
// work order and Invoicing did not. Neither searched the vendor's invoice number
// or the carrier's tracking number, which are the two Colby added at the gate.
// A fourth copy was the thing to avoid; a single list every screen imports is
// the thing that makes "whichever number you are holding, the order comes back"
// true rather than nearly true.
//
// AND WHY THE SEARCH AND THE DISPLAY LIVE TOGETHER. #545 is the reason. The
// Requests inbox has advertised PO search in its own placeholder since #412 and
// has never shown a PO number on the record it returns — so a person could
// search by a number, get a row back, and have no way to confirm it was the
// right one. A search that can find a record the record cannot confirm is worse
// than no search. Keeping ORDER_IDENTIFIERS as the single source for both halves
// means a field cannot become searchable without becoming visible.

// All six live on `material_requests` — verified against db/SCHEMA_REFERENCE.md
// at 79.14d, not assumed. That is what makes one helper possible: every screen
// already holds a request row, whatever else it holds.
//
// Order matters and is not alphabetical. It runs from Stede's own identifier
// outwards to the ones other companies gave us: our request number, our PO, the
// vendor's order number, the job it is for, the vendor's bill, the carrier's
// tracking. The eye lands in the same place on every record, which is half of
// what D15 is for.
export const ORDER_IDENTIFIERS = [
  { key: 'request_number', label: 'Request', get: r => r?.request_number },
  { key: 'po_ref', label: 'PO', get: r => r?.po_ref },
  { key: 'vendor_order_ref', label: 'Vendor order', get: r => r?.vendor_order_ref },
  { key: 'work_order_ref', label: 'Work order', get: r => r?.work_order_ref },
  { key: 'invoice_ref', label: 'Invoice', get: r => r?.invoice_ref },
  { key: 'tracking_number', label: 'Tracking', get: r => r?.tracking_number },
];

function clean(v) {
  const s = v == null ? '' : String(v).trim();
  return s === '' ? null : s;
}

// D15. ALWAYS returns all six, with `value: null` for the ones this record does
// not have, because the caller's job is to render a labelled dash rather than to
// decide whether the field exists. Colby's ruling: "show something like a - or
// 'not recorded' if their not so it doesnt look like the layout problems to a
// user." A field that silently vanishes reads as a rendering fault; a labelled
// dash reads as a fact somebody checked.
//
// This REVERSES a written decision in the receive picker, whose own comment
// justifies rendering each identifier "only when present rather than printing an
// empty separator." That reasoning was about punctuation and is still right about
// punctuation — it was avoiding a dangling separator between two chips. A
// labelled chip prints no stray separator and still tells the reader the number
// does not exist, so the two concerns come apart cleanly. The old comment is
// amended in the same commit rather than left contradicting this.
export function orderIdentifiers(req) {
  return ORDER_IDENTIFIERS.map(f => ({
    key: f.key,
    label: f.label,
    value: clean(f.get(req)),
  }));
}

// Every invoice number reachable from this row.
//
// THE HEADER FIELD IS A MIRROR OF THE MOST RECENT INVOICE ONLY, and that is
// stated outright in 20260819_partial_invoicing.sql: "Header invoice_ref /
// invoiced_at / invoiced_by are kept as mirrors of the most recent invoice."
// So on an order billed across three invoices, `material_requests.invoice_ref`
// carries the third and a person holding the first would not find it by the
// header alone. Where a screen has loaded the invoice rows — Invoicing does —
// every number is searchable. Where it has not, the mirror is what there is.
// See #304's gate, D6, amended at 79.14d.
export function invoiceRefsFor(req) {
  const out = [];
  const header = clean(req?.invoice_ref);
  if (header) out.push(header);
  for (const inv of req?.material_request_invoices || []) {
    const ref = clean(inv?.invoice_ref);
    if (ref && !out.includes(ref)) out.push(ref);
  }
  return out;
}

// Everything a person could type that should return this order. The six
// identifiers plus the vendor's name — the obvious non-numeric one, and the one
// all three existing searches already had.
export function orderSearchTerms(req) {
  const terms = [];
  for (const f of ORDER_IDENTIFIERS) {
    if (f.key === 'invoice_ref') continue;
    const v = clean(f.get(req));
    if (v) terms.push(v);
  }
  terms.push(...invoiceRefsFor(req));
  const vendor = clean(req?.inventory_vendors?.name);
  if (vendor) terms.push(vendor);
  return terms;
}

// Substring, case-insensitive, matching every existing screen's behaviour so
// nobody's search gets narrower than it was. An empty term matches everything,
// which is what lets a caller use this as an unconditional filter.
export function matchesOrderSearch(req, term) {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return true;
  return orderSearchTerms(req).some(v => v.toLowerCase().includes(q));
}

// Which identifier the person actually searched by, so the screen can mark that
// one chip and the eye goes straight to the number they were holding. Returns
// null when the match came from the vendor's name or when nothing matched —
// both are legitimate and neither should highlight a number.
export function matchedIdentifierKey(req, term) {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return null;
  for (const f of ORDER_IDENTIFIERS) {
    if (f.key === 'invoice_ref') {
      if (invoiceRefsFor(req).some(v => v.toLowerCase().includes(q))) return 'invoice_ref';
      continue;
    }
    const v = clean(f.get(req));
    if (v && v.toLowerCase().includes(q)) return f.key;
  }
  return null;
}

// The columns a screen must SELECT for the search above to be able to fire.
// Exported as a string because that is the shape a Supabase select takes, and
// because CLAUDE.md's own rule is that this codebase names its columns
// explicitly — so a migration changes nothing on its own and a matcher reading a
// column nobody selected fails SILENTLY, matching nothing and looking exactly
// like an order that is not there. Every one of the five surfaces was checked
// against this list at 79.14d; the Requests inbox and the receive page were both
// missing two.
export const ORDER_IDENTIFIER_COLUMNS =
  'request_number, po_ref, vendor_order_ref, work_order_ref, invoice_ref, tracking_number';
